/**
 * BiddingTakeoffProposalPanel — staff review of agent-proposed draft take-off links.
 *
 * The Mac-mini bidding agent writes proposals (status: pending). Staff approve
 * each (creating a real pdfTakeoffLink via pdfTakeoffLinkService) or reject it.
 * Agents never push take-off into the proposal/measurement workflow unapproved.
 */

import React, { useEffect, useState, useCallback } from 'react';
import type { BiddingTakeoffProposal } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import {
  subscribeLeadTakeoffProposals,
  approveTakeoffProposal,
  rejectTakeoffProposal,
} from '../../utils/biddingTakeoffProposalService';
import { Loader2, Check, X, MapPin, ClipboardList } from 'lucide-react';
import './BiddingTakeoffProposalPanel.css';

interface BiddingTakeoffProposalPanelProps {
  leadId: string;
  /** Deep-link the proposal's plan location in the document viewer, if available. */
  onViewOnPlan?: (proposal: BiddingTakeoffProposal) => void;
}

function summarizeLink(p: BiddingTakeoffProposal): string {
  const l = p.proposedLink || {};
  const parts: string[] = [];
  if (l.quantity != null) parts.push(`${l.quantity}x`);
  if (l.dimensions) parts.push(l.dimensions);
  if (l.species) parts.push(l.species);
  const head = parts.join(' ').trim();
  return [l.name, head].filter(Boolean).join(' · ') || 'Take-off item';
}

export const BiddingTakeoffProposalPanel: React.FC<BiddingTakeoffProposalPanelProps> = ({
  leadId,
  onViewOnPlan,
}) => {
  const { user } = useAuth();
  const [proposals, setProposals] = useState<BiddingTakeoffProposal[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) return;
    const unsub = subscribeLeadTakeoffProposals(leadId, setProposals, { pendingOnly: true });
    return () => unsub();
  }, [leadId]);

  const handleApprove = useCallback(
    async (p: BiddingTakeoffProposal) => {
      if (!p.id || !user) return;
      setBusyId(p.id);
      setError(null);
      try {
        await approveTakeoffProposal(p, { userId: user.id, userName: user.name || user.email });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Approve failed');
      } finally {
        setBusyId(null);
      }
    },
    [user],
  );

  const handleReject = useCallback(
    async (p: BiddingTakeoffProposal) => {
      if (!p.id || !user) return;
      setBusyId(p.id);
      setError(null);
      try {
        await rejectTakeoffProposal(p.id, { userId: user.id, userName: user.name || user.email });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Reject failed');
      } finally {
        setBusyId(null);
      }
    },
    [user],
  );

  if (proposals.length === 0) return null;

  return (
    <section className="btp2-root" data-tour="cw-takeoff-proposals">
      <h4 className="btp2-title">
        <ClipboardList size={13} />
        Draft Take-off Proposals
        <span className="btp2-count">{proposals.length}</span>
      </h4>
      <p className="btp2-hint">
        The bidding agent proposed these take-off links. Approve to add them to the deal take-off, or reject.
      </p>
      {error && <div className="btp2-error">{error}</div>}
      <ul className="btp2-list">
        {proposals.map(p => {
          const busy = busyId === p.id;
          const hasPlanRef = !!(p.documentId && (p.annotationId || p.pageNumber != null));
          return (
            <li key={p.id} className="btp2-item">
              <div className="btp2-item-main">
                <div className="btp2-item-summary">{summarizeLink(p)}</div>
                <div className="btp2-item-meta">
                  <span>Page {p.pageNumber}</span>
                  {typeof p.confidence === 'number' && (
                    <span>· {Math.round(p.confidence * 100)}% conf.</span>
                  )}
                </div>
                {p.rationale && <div className="btp2-item-rationale">{p.rationale}</div>}
              </div>
              <div className="btp2-item-actions">
                {hasPlanRef && onViewOnPlan && (
                  <button
                    type="button"
                    className="btp2-view"
                    onClick={() => onViewOnPlan(p)}
                    title="View on plan"
                  >
                    <MapPin size={12} />
                  </button>
                )}
                <button
                  type="button"
                  className="btp2-approve"
                  disabled={busy || !user}
                  onClick={() => handleApprove(p)}
                  title="Approve — create take-off link"
                >
                  {busy ? <Loader2 size={12} className="btp2-spin" /> : <Check size={12} />}
                </button>
                <button
                  type="button"
                  className="btp2-reject"
                  disabled={busy || !user}
                  onClick={() => handleReject(p)}
                  title="Reject"
                >
                  <X size={12} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default BiddingTakeoffProposalPanel;
